import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Heart, Globe, Award, Zap } from 'lucide-react';

const About: React.FC = () => {
  return (
    <section id="about" className="py-24 relative overflow-hidden">
      <div className="container mx-auto px-6">
        <div className="grid lg:grid-cols-12 gap-16 items-center">
          
          {/* Visual Column - Official Premium Portrait */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-5 relative"
          >
            <div className="relative z-10 rounded-[3rem] overflow-hidden aspect-[2/3] glass border-white/5 p-2 shadow-2xl bg-black">
              <div className="absolute inset-0 bg-gradient-to-tr from-dark via-transparent to-gold/10 z-10 pointer-events-none" />
              
              <img 
                src="images/dj-saff-afrobeats-platine.png" 
                alt="Portrait DJ SAFF "
                className="w-full h-full object-cover rounded-[2.5rem] brightness-90 transition-all duration-1000"
              />
              
              <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent opacity-60" />
            </div>
            
            {/* Professional Certification Badge */}
            <motion.div 
              initial={{ opacity: 0, scale: 0.8, rotate: -10 }}
              whileInView={{ opacity: 1, scale: 1, rotate: 0 }}
              viewport={{ once: true }}
              className="absolute -bottom-6 -right-6 z-20 glass p-6 rounded-2xl border-gold/30 shadow-[0_0_30px_rgba(180,133,5,0.2)] flex flex-col items-center"
            >
              <Award className="text-gold w-8 h-8 mb-2" />
              <p className="text-[8px] font-bold uppercase tracking-[0.2em] text-white">Certifié</p>
              <p className="text-[10px] font-black uppercase tracking-widest text-gold text-center">Titre Pro RNCP<br/>Avril 2024</p>
            </motion.div>

            <div className="absolute -top-20 -left-20 w-80 h-80 bg-gold/5 blur-[120px] rounded-full opacity-50" />
          </motion.div>

          {/* Content Column */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1 }}
            className="lg:col-span-7 space-y-8"
          >
            <div className="space-y-4">
              <div className="inline-flex items-center space-x-3">
                <div className="h-px w-8 bg-gold" />
                <span className="text-gold text-[10px] font-bold tracking-[0.4em] uppercase">L'homme derrière les platines</span>
              </div>
              <h2 className="text-5xl md:text-7xl font-serif font-bold leading-tight">
                Issa, alias <br />
                <span className="text-gold-gradient italic">DJ SAFF.</span>
              </h2>
            </div>

            <div className="space-y-6 text-white/70 font-light leading-relaxed text-sm md:text-base">
              <p className="text-xl text-white font-serif italic">
                "Je m’appelle Issa… mais derrière les platines, vous me connaissez peut-être sous le nom de DJ SAFF. ou de Free Events."
              </p>
              <p>
                Mon histoire avec la musique commence en 2000, à mon arrivée en France. Très vite, <span className="txt-important">le mix devient plus qu’une passion : un langage, une énergie, une façon de rassembler.</span>
              </p>
              <p>
                Depuis plus de vingt ans, je vis ici, entre cultures et influences. Cette richesse se retrouve dans mes sets : <span className="txt-important">une capacité à naviguer entre les styles, à connecter les générations, et à créer une ambiance qui parle à tous.</span>
              </p>
              <p>
                Aujourd’hui, <span className="txt-important">je suis un DJ complet.</span>
              </p>
              <p>
                À l’aise aussi bien sur un <span className="txt-important">mariage</span>, un <span className="txt-important">anniversaire</span> ou un <span className="txt-important">événement d’entreprises</span>, que derrière les platines <span className="txt-important">en club</span>. Mon approche reste la même : lire la foule, comprendre l’instant, et faire monter l’énergie au bon moment.
              </p>
              <p>
                Afrobeats, Amapiano, Afrohouse, hits généralistes, classiques intemporels… <span className="txt-important">Je mélange les univers</span> pour créer des expériences uniques, élégantes ou explosives selon l’événement.
              </p>
              <p className="border-l-2 border-gold pl-6 py-2 bg-white/5 rounded-r-2xl italic">
                Autodidacte à mes débuts, j’ai affiné mon savoir-faire sur le terrain avant de le renforcer chez DJ Network, où j’ai obtenu en avril 2024 le Certificat de Compétences Professionnelles (RNCP).
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 pt-6">
              <div className="glass p-6 rounded-2xl border-white/5 flex flex-col items-center text-center space-y-2">
                <Globe className="text-gold w-6 h-6" />
                <span className="text-[10px] font-bold uppercase tracking-widest">Double Culture</span>
              </div>
              <div className="glass p-6 rounded-2xl border-white/5 flex flex-col items-center text-center space-y-2">
                <Heart className="text-gold w-6 h-6" />
                <span className="text-[10px] font-bold uppercase tracking-widest">Passion & Émotion</span>
              </div>
              <div className="glass p-6 rounded-2xl border-white/5 flex flex-col items-center text-center space-y-2">
                <Zap className="text-gold w-6 h-6" />
                <span className="text-[10px] font-bold uppercase tracking-widest">Mix Universel</span>
              </div>
            </div>

            <div className="pt-4 flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 rounded-full border border-gold/30 p-1 flex items-center justify-center bg-dark">
                   <span className="text-gold font-serif italic text-lg">S</span>
                </div>
                <div>
                  <p className="font-bold text-sm tracking-widest uppercase">DJ SAFF</p>
                  <p className="text-[10px] text-gold uppercase tracking-[0.3em]">Créer l’ambiance juste & Faire vibrer le moment</p>
                </div>
              </div>
              <a href="#contact" className="hidden sm:flex items-center space-x-2 text-gold font-bold tracking-widest text-[10px] uppercase group">
                <span>Contacter DJ Saff</span>
                <ArrowRight size={14} className="group-hover:translate-x-2 transition-transform" />
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default About;